import React, { useCallback, useContext, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FunctionMicroFrontend } from "@securitize/reactjs-mfe";
import { ProfileContext } from "../../../contexts/ProfileContext";
import { UserContext } from "../../../contexts/UserContext";
import { MFEContext } from "../../../contexts/MFEContext";
import { ErrorPage } from "../../shared/ErrorPage";
import {
  ProfilePermissionType,
  ProfilePermissionTypes,
} from "../../../data/dtos/profile-config.dto";
import { UserVerificationNeeded } from "../information/content-restricted-pages/UserVerificationNeeded";
import { useResolvedPath } from "react-router";

const ENTRY_POINT_URL = process.env.REACT_APP_TA_TAX_CENTER_MFE_ENTRY_POINT_URL;

const TaxCenterMFE = ({
  isPendingActionsLoading,
  refetchPendingActions,
  permission,
}: {
  isPendingActionsLoading: boolean;
  refetchPendingActions: () => void;
  permission: ProfilePermissionType;
}) => {
  const { language } = useContext(ProfileContext);
  const { userInfo } = useContext(UserContext);
  const { mixPanel, router } = useContext(MFEContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname: currentPath } = useResolvedPath("");

  const renderParams = useMemo(
    () => ({
      language,
      userInfo,
      MixPanel: mixPanel,
      basePath: "/profile",
      onUpdate: refetchPendingActions,
    }),
    [language, userInfo, mixPanel, refetchPendingActions],
  );

  const mountParams = useMemo(
    () => ({
      onNavigate: ({
        pathname: nextPathname,
        search,
      }: {
        pathname: string;
        search?: string;
      }) => {
        if (location.pathname !== nextPathname) {
          navigate({ pathname: nextPathname, search });
        }
      },
      basePath: "/profile",
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount params are executed only once
    [],
  );

  const afterMount = useCallback(
    ({
      onParentNavigate,
    }: {
      onParentNavigate: (_: {
        pathname: string;
        search: string;
        state: unknown;
      }) => void;
    }) => {
      router.subscribe((subscriber) =>
        onParentNavigate({
          pathname: subscriber.location.pathname,
          search: subscriber.location.search,
          state: subscriber.location.state,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount params are executed only once
    [],
  );

  if (isPendingActionsLoading) return null;

  if (permission === ProfilePermissionTypes.USER_VERIFICATION_NOT_SUBMITTED) {
    return (
      <UserVerificationNeeded
        isVerificationInReview={false}
        currentPath={currentPath}
      />
    );
  }

  if (permission === ProfilePermissionTypes.USER_VERIFICATION_PENDING) {
    return (
      <UserVerificationNeeded
        isVerificationInReview
        currentPath={currentPath}
      />
    );
  }

  return (
    <FunctionMicroFrontend
      entryPointUrl={ENTRY_POINT_URL ?? ""}
      exposedPath="./TaxCenterApp"
      name="taTaxCenterFe"
      afterMount={afterMount}
      mountParams={mountParams}
      renderParams={renderParams}
      errorContent={<ErrorPage />}
    />
  );
};

export default TaxCenterMFE;
